import * as Msal from '@azure/msal-browser';
import { BaseAuthRequest } from '@azure/msal-common';
import { StringDict } from '@azure/msal-common';
import { User } from '../interfaces/general';
import { SpaConfiguration } from '@configuration/SpaConfiguration';
import { MultitenantProvider } from './MultitenantProvider';
import { getClient } from '../Functions';
import { getAppInsights } from '@providers/TelemetryProvider/TelemetryService';
export class Authentication {
    private static _msalInstance: Msal.PublicClientApplication;
    private static _user: User = null;
    private static _loginRequest: Msal.RedirectRequest = {
        scopes: ["openid", "profile"]
    };
    private static readonly _redirectUrl: string = "https://client.talxis.com/shared/sso.html";
    public static getUser(): User {
        return this._user;
    }
    public static isAuthenticated(): boolean {
        return this._user !== null;
    }
    private static async _getTokenRequest(): Promise<Omit<BaseAuthRequest, "authority" | "correlationId">> {
        return {
            scopes: SpaConfiguration.get().apiScopes
        };
    }
    private static _setAppInsightsUserContext(): void {
        const ai = getAppInsights();
        if (ai && this._user) {
            ai.setAuthenticatedUserContext(this._user.accessPrincipalId, null, true);
        }
    }
    private static _clearAppInsightsUserContext(): void {
        const ai = getAppInsights();
        if (ai) {
            ai.clearAuthenticatedUserContext();
        }
    }
    public static async trySilentLoginAsync(): Promise<boolean> {
        await this._getMsalInstanceAsync();

        let tokenResponse = await this._msalInstance.handleRedirectPromise();
        // We are returning from authorization server, run the proper login pipeline
        if (tokenResponse) {
            return this.loginAsync();
        }
        let accounts = this._msalInstance.getAllAccounts();
        let token: Msal.AuthenticationResult;

        if (accounts.length === 1) {
            this._msalInstance.setActiveAccount(accounts[0]);
            try {
                token = await this._msalInstance.acquireTokenSilent(await this._getTokenRequest());
            }
            catch (err) {
            }
        }
        else if (accounts.length > 1) {
            // Attempt to match one of many accounts to the current authority
            let foundAccount = false;
            const spaConfiguration = SpaConfiguration.get();
            const environment = new URL(spaConfiguration.authority);
            for (const account of accounts) {
                if (account.environment === environment.hostname) {
                    this._msalInstance.setActiveAccount(account);
                    try {
                        token = await this._msalInstance.acquireTokenSilent(await this._getTokenRequest());
                        accounts = [account];
                        foundAccount = true;
                        break;
                    }
                    catch (err) {
                    }
                }
            }
            if (!foundAccount) {
                // If there are multiple accounts, log all the accounts out locally (clear cache)
                for (const account of accounts) {
                    this._msalInstance.logoutRedirect({
                        onRedirectNavigate: (url) => {
                            return false;
                        },
                        account: account
                    });
                }
            }
        }

        if (token) {
            const user = accounts[0];

            let response: Response;
            try {
                response = await fetch(SpaConfiguration.get().edsApi + '/v9.1/whoami', {
                    headers: {
                        ...MultitenantProvider.getFetchHeader().headers,
                        ...(await this.getAuthorizationHeader(true)).headers,
                    }
                });
            }
            catch (error) {
                // Only network errors come here
                throw new Error("Network error - failed to reach the backend API " + SpaConfiguration.get().edsApi);
            }

            if (!response.ok) {
                return false;
            }

            var responseContent = await response.json();
            this._user = {
                accessPrincipalId: responseContent.accessPrincipalId,
                userPrincipalName: responseContent.userPrincipalName,
                displayName: responseContent.displayName,
                msalUser: user
            };
            Authentication._setAppInsightsUserContext();
            return true;
        }

        return false;
    }
    public static async loginAsync(popup?: boolean): Promise<boolean> {
        if (popup) {
            throw new Error('Popup login is currently not supported!');
        }
        await this._getMsalInstanceAsync();
        let tokenResponse = await this._msalInstance.handleRedirectPromise();
        const accounts = this._msalInstance.getAllAccounts();
        let token: Msal.AuthenticationResult;
        // Check if we can successfully obtain a token
        if (accounts.length === 1) {
            this._msalInstance.setActiveAccount(accounts[0]);
            try {
                token = await this._msalInstance.acquireTokenSilent(await this._getTokenRequest());
            }
            catch (err) {
            }
        }
        else if (accounts.length > 1) {
            // If there are multiple accounts, log all the accounts out locally (clear cache)
            for (const account of accounts) {
                this._msalInstance.logoutRedirect({
                    onRedirectNavigate: (url) => {
                        return false;
                    },
                    account: account
                });
            }
        }
        if (!tokenResponse && !token) {
            const result = await this._handleLogin({ ...this._loginRequest, state: this._createState(window.location.href) });
            if (!result) return false;

            tokenResponse = result;
        }
        if (tokenResponse) {
            const user = tokenResponse.account;
            this._msalInstance.setActiveAccount(user);

            let originalUrl = this._getState(tokenResponse.state ?? "");
            if (originalUrl && originalUrl != "" && originalUrl != window.location.href) {
                window.location.replace(originalUrl);
            }
            else {
                let response: Response;
                try {
                    response = await fetch(SpaConfiguration.get().edsApi + '/v9.1/whoami', {
                        headers: {
                            ...MultitenantProvider.getFetchHeader().headers,
                            ...(await this.getAuthorizationHeader(true)).headers,
                        }
                    });
                }
                catch (error) {
                    // Only network errors come here
                    throw new Error("Network error - failed to reach the backend API");
                }

                if (!response.ok) {
                    var errorDetails = (response) ? (await response.json())?.message : "";
                    throw new Error('Authentication error! ' + errorDetails);
                }

                var responseContent = await response.json();
                this._user = {
                    accessPrincipalId: responseContent.accessPrincipalId,
                    userPrincipalName: responseContent.userPrincipalName,
                    displayName: responseContent.displayName,
                    msalUser: user
                };
                Authentication._setAppInsightsUserContext();
            }
            return true;
        }
        else if (token) {
            const user = accounts[0];

            let response: Response;
            try {
                response = await fetch(SpaConfiguration.get().edsApi + '/v9.1/whoami', {
                    headers: {
                        ...MultitenantProvider.getFetchHeader().headers,
                        ...(await this.getAuthorizationHeader(true)).headers,
                    }
                });
            }
            catch (error) {
                // Only network errors come here
                throw new Error("Network error - failed to reach the backend API " + SpaConfiguration.get().edsApi);
            }

            if (!response.ok) {
                // Potentially invalid token, force re-authentication
                await this._handleLogin({ ...this._loginRequest, state: this._createState(window.location.href) });
                return false;
            }

            var responseContent = await response.json();
            this._user = {
                accessPrincipalId: responseContent.accessPrincipalId,
                userPrincipalName: responseContent.userPrincipalName,
                displayName: responseContent.displayName,
                msalUser: user
            };
            Authentication._setAppInsightsUserContext();
            return true;
        }
    };
    /**
     * Internal force login handle - either redirect, attempt silent SSO or create a pop-up
     */
    private static async _handleLogin(request?: Msal.RedirectRequest): Promise<Msal.AuthenticationResult> {
        if (getClient() !== "Outlook") {
            window.TALXIS.Portal.Context?.SetLoading(true);
            this._msalInstance.loginRedirect(request);
            return null;
        }
        else {
            // TODO: This should be UPN, however EXO returns primary email address, in case it is different, we will need to go with pop-up since the SSO will fail
            const username = Office.context.mailbox.userProfile.emailAddress;
            try {
                const result = await this._msalInstance.ssoSilent({ ...request, loginHint: username });
                return result;
            }
            catch (err) {
                console.error("Failed to obtain token via ssoSilent!", err);
                // TODO: Attempt to login via pop-up
                throw err;
            }
        }
    }
    public static async loginRedirect(extraQueryParameters?: StringDict, redirectStartPage?: string, domainHint?: string, authority?: string): Promise<void> {
        await this._getMsalInstanceAsync();

        const tokenResponse = await this._msalInstance.handleRedirectPromise();

        if (tokenResponse) {
            let originalUrl = this._getState(tokenResponse.state ?? "");
            if (originalUrl && originalUrl != "" && originalUrl != window.location.href) {
                window.location.replace(originalUrl);
            }
        }
        else {
            const request: Msal.RedirectRequest = {
                ...this._loginRequest,
                state: this._createState(redirectStartPage ? redirectStartPage : null),
                extraQueryParameters: extraQueryParameters,
                authority: authority
            };

            if (domainHint) {
                request.domainHint = domainHint;
            }

            return this._msalInstance.loginRedirect(request);
        }
    };
    public static async getAuthorizationHeader(requireToken: boolean = false): Promise<RequestInit> {
        const request: RequestInit = {
            headers: {
                ...((this.isAuthenticated() || requireToken) && { 'Authorization': `Bearer ${await this._getTokenAsync()}` })
            }
        };
        return request;
    }
    public static async logout(): Promise<void> {
        this._clearAppInsightsUserContext();
        await this._msalInstance.logoutRedirect();
    }
    public static async profileEditRedirect(): Promise<void> {
        await this._getMsalInstanceAsync();
        if (this._msalInstance.getAllAccounts().length === 1) {
            if (this._isB2c()) {
                const spaConfiguration = SpaConfiguration.get();
                this._msalInstance.loginRedirect({ ...this._loginRequest, state: this._createState(window.location.href), authority: spaConfiguration.authority + spaConfiguration.b2cProfileEditPolicy });
            }
            else {
                window.open("https://myaccount.microsoft.com", "_blank");
            }
        }
    }
    public static async isProfileEditRedirectAvailable(): Promise<boolean> {
        await this._getMsalInstanceAsync();
        if (this._msalInstance.getAllAccounts().length === 1) {
            if (this._isB2c()) {
                const spaConfiguration = SpaConfiguration.get();
                if (spaConfiguration.b2cProfileEditPolicy && spaConfiguration.b2cProfileEditPolicy !== "") {
                    return true;
                }
                else {
                    return false;
                }
            }
            else {
                return true;
            }
        }
    }
    public static async passwordChangeRedirect(): Promise<void> {
        await this._getMsalInstanceAsync();
        if (this._msalInstance.getAllAccounts().length === 1) {
            if (this._isB2c()) {
                const spaConfiguration = SpaConfiguration.get();
                this._msalInstance.loginRedirect({ ...this._loginRequest, prompt: "login", state: this._createState(window.location.href), authority: spaConfiguration.authority + spaConfiguration.b2cPasswordChangePolicy });

            }
            else {
                window.open("https://account.activedirectory.windowsazure.com/ChangePassword.aspx", "_blank");
            }
        }
    }
    private static _getMsalInstanceAsync(): void {
        if (this._msalInstance) return;

        const spaConfiguration = SpaConfiguration.get();
        const msalConfig: Msal.Configuration = {
            auth: {
                clientId: spaConfiguration.clientId,
                authority: spaConfiguration.authority + (this._isB2c() ? spaConfiguration.b2cLoginPolicy : ""),
                redirectUri: this._redirectUrl,
                knownAuthorities: [spaConfiguration.authority],
            },
            cache: {
                cacheLocation: "localStorage",
                storeAuthStateInCookie: false
            }
        };
        this._msalInstance = new Msal.PublicClientApplication(msalConfig);
    }
    private static async _getTokenAsync(): Promise<string> {
        try {
            const response = await this._msalInstance.acquireTokenSilent(await this._getTokenRequest());
            return response.accessToken;
        }
        catch (err) {
            this._msalInstance.loginRedirect({ ...this._loginRequest, state: this._createState(window.location.href) });
            throw err;
        }
    }
    private static _isB2c(): boolean {
        const spaConfiguration = SpaConfiguration.get();
        if (spaConfiguration.b2cLoginPolicy) {
            return true;
        }
        else {
            return false;
        }
    }
    private static _createState(customData: any): string {
        const state: IPortalState = {
            __ssoRedirectUrl: `${window.location.protocol}//${window.location.host}`,
            __customData: customData
        };
        return btoa(JSON.stringify(state));
    }
    private static _getState(state: string): any {
        try {
            return (JSON.parse(atob(state)) as IPortalState).__customData;
        }
        catch (e) {
            console.warn("Failed to parse and deserialize incoming state", state);
            return null;
        }
    }
};

export const requireAuthentication = async (): Promise<boolean> => {
    if (Authentication.isAuthenticated()) {
        return false;
    }
    else {
        return Authentication.loginAsync();
    }
};

interface IPortalState {
    __ssoRedirectUrl: string;
    __customData: any;
}